// Queue contracts (ADR 0013 §11), mirrored from the API: GET /v1/queue returns
// household-day BUNDLES (items sharing a bundle_key are one household-touch), and
// PATCH /v1/queue-items/:id is a column-limited claim/complete/dismiss transition.
import type { MessageKey } from "../../i18n/dictionaries";

export type PriorityBand = "HIGH" | "MEDIUM" | "LOW";
// NEW_LEAD (source_kind NEW_LEAD, migration 0047) — a self-serve inbound lead minted
// AS a queue_item at lead-creation, so it lands in the daily queue like any other item.
export type QueueItemType = "RENEWAL" | "OPPORTUNITY" | "ALERT" | "SERVICE_FOLLOWUP" | "NEW_LEAD";
export type QueueItemStatus = "PENDING" | "IN_PROGRESS" | "DONE" | "DISMISSED" | "SUPPRESSED";

export interface QueueItem {
  id: string;
  householdId: string;
  partyId: string | null;
  ownerAdvisorId: string;
  itemType: QueueItemType;
  priorityScore: string; // numeric(6,2) as a string — never floated
  priorityBand: PriorityBand;
  bundleKey: string;
  // The soft reference to the row that minted this item (queue_item.source_ref;
  // no FK). For a NEW_LEAD item this is the originating lead's id — the handle the
  // console opens "3 Teklif Hazırla" against (POST /v1/leads/:leadId/prepare-offers).
  sourceRef: string | null;
  status: QueueItemStatus;
  dueAt: string;
  slaDueAt: string | null;
  disposition: string | null;
  dispositionNote: string | null;
  createdAt: string;
  // FE-1 read enrichment — the two glance fields the enriched queue read decorates
  // every row with (queue-read.service EnrichedQueueItemRow): the party display name
  // and the intake-captured VEHICLE plate. The NON-sensitive projection (ADR 0002
  // cond.1). Nullable: an item may carry no party (partyId nullable) or no vehicle.
  partyName: string | null;
  plate: string | null;
  // The intake channel that minted a NEW_LEAD item ("web" | "phone" | …), decoded
  // from the source_signature by the enriched queue read. Drives the source-accurate
  // badge (Web / Telefon). Null for non-NEW_LEAD items.
  source: string | null;
}

/** One household-touch: every item sharing a bundle_key, ranked by its top item. */
export interface QueueBundle {
  bundleKey: string;
  householdId: string;
  ownerAdvisorId: string;
  priority: number;
  items: QueueItem[];
}

export type QueueAction = "CLAIM" | "COMPLETE" | "DISMISS";

export interface TransitionInput {
  action: QueueAction;
  disposition?: string;
  reason?: string;
}

export interface QueueFilter {
  band?: PriorityBand;
  type?: QueueItemType;
}

export const PRIORITY_BANDS: PriorityBand[] = ["HIGH", "MEDIUM", "LOW"];
export const QUEUE_ITEM_TYPES: QueueItemType[] = [
  "RENEWAL",
  "OPPORTUNITY",
  "ALERT",
  "SERVICE_FOLLOWUP",
  "NEW_LEAD",
];

/** i18n key for an item type's human "suggestion" label. */
export const itemTypeLabelKey = (t: QueueItemType): MessageKey => `item.${t}` as MessageKey;
export const bandLabelKey = (b: PriorityBand): MessageKey => `band.${b}` as MessageKey;

/** i18n key for a SETTLED item's terminal status (GAP-AUDIT-3 D7). A DONE /
 *  DISMISSED / SUPPRESSED row has nothing left to act on, so the action buttons
 *  are replaced with this calm label rather than left live to fail server-side. */
export const queueStatusLabelKey = (s: QueueItemStatus): MessageKey =>
  `queue.status.${s}` as MessageKey;

/** An inbound-intake lead (web self-serve OR advisor phone door), now materialized
 *  as a queue_item (source_kind NEW_LEAD). It carries a source-accurate badge
 *  (Web / Telefon) in the daily queue and the "3 Teklif Hazırla" action. The name
 *  is historical (web was the first door); it means "is a NEW_LEAD item". */
export const isWebLeadItem = (item: QueueItem): boolean => item.itemType === "NEW_LEAD";

/** i18n key for a NEW_LEAD item's source-accurate intake badge: "Telefon" for a
 *  phone-door lead, "Web" for a self-serve web lead (also the fallback when the
 *  source is absent/unknown, since web was the original inbound channel). */
export const leadSourceLabelKey = (item: QueueItem): MessageKey =>
  item.source === "phone" ? "leads.source.phone" : "leads.source.web";

/** The originating lead id of a web (NEW_LEAD) queue item — carried on source_ref.
 *  Null for any non-lead item (or a lead item that somehow lacks the reference),
 *  so callers gate the "3 Teklif Hazırla" action on a truthy result. */
export const webLeadId = (item: QueueItem): string | null =>
  isWebLeadItem(item) ? item.sourceRef : null;
